import { useId } from 'preact/hooks'
import { Clock, UserCheck } from 'lucide-preact'
import type { SendingDomainViewDto, SendingDomainWarmupTodayDto, SendingDomainWarmupViewDto } from '@/types/sendingDomains'
import { sendingDomainPill } from '@/types/sendingDomains'
import { formatCount, localShortDay, localTimeWithZone, moveUpRuleSentence, stepLimitsSentence } from '@/utils/sendingDomainWarmup'
import {
  BOTH_CHECKS_PASSED,
  OVER_LIMIT_WAIT_SENTENCE,
  REGISTRANTS_NEVER_HELD,
  noLimitFromPhrase,
  regionLabel,
  restartLine,
  sharedLimitSentence,
  todayMarkPhrase,
  todayMeterLabel,
  warmingVerdict,
  warmupStep,
} from '@/utils/sendingDomainStatus'
import { BlockHeading, DomainStatusBox, WarmupLoading, type StatusFact } from './DomainPageParts'
import { DomainStatePill } from './DomainStatePill'
import { DomainSuggestions } from './DomainSuggestions'
import { LastThreeDays } from './LastThreeDays'
import { WarmupLadder } from './WarmupLadder'
import { SECONDARY_BUTTON } from './styles'

interface DomainWarmingViewProps {
  domain: SendingDomainViewDto
  warmup: SendingDomainWarmupViewDto | null
  warmupError: string | null
  busy: boolean
  onRestart: () => void
}

/** A warming (or holding) domain: where it is on the warm-up, today's sends against the limit, and its last 3 days. */
export function DomainWarmingView({ domain, warmup, warmupError, busy, onRestart }: DomainWarmingViewProps) {
  const headingId = useId()
  const pill = sendingDomainPill(domain)
  if (!warmup) return <WarmupLoading error={warmupError} />
  if (pill !== 'Warming' && pill !== 'Holding') return null
  const step = warmupStep(domain)
  if (!step) return null

  const noLimit = noLimitFromPhrase(warmup)
  const facts: StatusFact[] = [
    { label: 'Status', value: <DomainStatePill domain={domain} /> },
    { label: 'Region', value: regionLabel(domain.Region) },
  ]
  if (domain.WarmupStartedAtUtc) facts.push({ label: 'Warm-up started', value: localShortDay(domain.WarmupStartedAtUtc) })
  if (noLimit) facts.push({ label: 'No limit from', value: noLimit })
  facts.push({ label: 'Checks', value: BOTH_CHECKS_PASSED })

  return (
    <div className="space-y-10">
      <DomainStatusBox pill={pill} verdict={warmingVerdict(domain, warmup)} facts={facts} />

      <section aria-labelledby={headingId} className="space-y-6">
        <div className="space-y-1">
          <h3 id={headingId} className="text-base font-semibold text-slate-900 dark:text-white">Warm-up</h3>
          <p className="max-w-prose text-sm text-slate-600 dark:text-slate-400">
            <span className="font-medium text-slate-900 dark:text-white">
              {stepLimitsSentence(step.step, step.lastStep, step.dailyLimit, step.hourlyLimit)}
            </span>{' '}
            {moveUpRuleSentence(step.qualifyingVolume, warmup.RateLimits)}
          </p>
          <p className="max-w-prose text-sm text-slate-600 dark:text-slate-400">{sharedLimitSentence(warmup.Steps)}</p>
        </div>

        <div className="space-y-10">
          {warmup.Today && <TodayMeter today={warmup.Today} />}

          <div className="space-y-3">
            <BlockHeading>Limits by step</BlockHeading>
            <WarmupLadder steps={warmup.Steps} position={{ kind: 'warming', step: step.step }} />
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pt-2">
              <button type="button" onClick={onRestart} disabled={busy} className={SECONDARY_BUTTON}>Restart warm-up</button>
              <p className="max-w-prose text-[13px] text-slate-600 dark:text-slate-400">{restartLine(warmup.Steps, { saysAdminsOnly: true })}</p>
            </div>
          </div>

          <LastThreeDays rates={warmup.LastThreeDays} limits={warmup.RateLimits} />
        </div>
      </section>

      <DomainSuggestions domain={domain.Domain} />
    </div>
  )
}

/** Today's sends against the day's limit, with the mark a day has to pass to count toward moving up. */
function TodayMeter({ today }: { today: SendingDomainWarmupTodayDto }) {
  const fill = Math.min(today.Dispatched / today.DailyLimit, 1) * 100
  const markAt = (today.QualifyingVolume / today.DailyLimit) * 100

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="flex items-baseline gap-2">
          <span className="text-xl font-semibold tabular-nums text-slate-900 dark:text-white">{formatCount(today.Dispatched)}</span>
          <span className="text-sm text-slate-700 dark:text-slate-300">of {formatCount(today.DailyLimit)} sent today</span>
        </p>
        <p className="text-[13px] text-slate-600 dark:text-slate-400">Resets at {localTimeWithZone(today.ResetsAtUtc)}</p>
      </div>
      <div role="img" aria-label={todayMeterLabel(today)} className="relative h-2 w-full rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="h-full rounded-full bg-blue-500" style={{ width: `${fill}%` }} />
        <span className="absolute -top-1 h-4 w-0.5 bg-slate-600 dark:bg-slate-300" style={{ left: `${markAt}%` }} />
      </div>
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-slate-600 dark:text-slate-400">
        <span><span className="font-medium tabular-nums text-slate-900 dark:text-white">{formatCount(today.LeftToday)}</span> left today</span>
        {today.HourlyLimit != null && (
          <>
            <span aria-hidden="true">·</span>
            <span>At most <span className="font-medium tabular-nums text-slate-900 dark:text-white">{formatCount(today.HourlyLimit)}</span> an hour</span>
          </>
        )}
        <span aria-hidden="true">·</span>
        <span>{todayMarkPhrase(today)}</span>
      </p>
      <ul className="space-y-2">
        <li className="flex max-w-prose items-start gap-2 text-[13px] text-slate-600 dark:text-slate-400">
          <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
          <span>{OVER_LIMIT_WAIT_SENTENCE}</span>
        </li>
        <li className="flex max-w-prose items-start gap-2 text-[13px] text-slate-600 dark:text-slate-400">
          <UserCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden="true" />
          <span>
            <span className="font-medium text-slate-800 dark:text-slate-200">{REGISTRANTS_NEVER_HELD.lead}</span>{' '}
            {REGISTRANTS_NEVER_HELD.text}
          </span>
        </li>
      </ul>
    </div>
  )
}
